import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BookOpen, ChevronDown, LogOut, Menu, ShieldCheck, X } from "lucide-react";
import { useLang } from "../i18n";
import { useAuth } from "../auth";

const Nav = () => {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const links = [
    { to: "/storie", label: t("nav_stories"), id: "nav-link-stories" },
    { to: "/#shorts", label: t("nav_shorts"), id: "nav-link-shorts", anchor: true },
    { to: "/autore", label: t("nav_author"), id: "nav-link-author" },
    { to: "/sostieni", label: t("nav_donate"), id: "nav-link-donate" },
    { to: "/contatti", label: t("nav_contact"), id: "nav-link-contact" },
  ];

  const goAnchor = (e) => {
    if (pathname === "/") {
      e.preventDefault();
      document.getElementById("shorts")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const doLogout = async () => {
    await logout();
    setMenuOpen(false);
    setProfileOpen(false);
    navigate("/");
  };

  // chiudi menu mobile e menu profilo a ogni cambio pagina
  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [pathname]);

  // chiudi il menu profilo cliccando fuori
  useEffect(() => {
    const onDown = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // blocca lo scroll della pagina quando il menu mobile è aperto
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const roleBadge = (
    <span
      data-testid="nav-profile-role"
      className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-mono2 uppercase tracking-widest ${
        user?.role === "admin" ? "bg-gold/15 text-gold border border-gold/40" : "bg-white/5 text-mute border border-white/15"
      }`}
    >
      {user?.role === "admin" ? t("role_admin") : t("role_reader")}
    </span>
  );

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-2.5 group shrink-0">
          <img src="/img/logo-mark.png" alt="Una vita da DJ" className="w-7 h-7 rounded-lg transition-transform duration-700 group-hover:rotate-180" />
          <span className="hidden sm:inline font-display font-bold tracking-tight text-sm sm:text-base">UNA VITA DA DJ</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.anchor && pathname !== "/" ? "/#shorts" : l.to}
              onClick={l.anchor ? goAnchor : undefined}
              data-testid={l.id}
              className="text-sm text-mist hover:text-ember transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center rounded-full border border-white/15 overflow-hidden" data-testid="language-toggle">
            {[["it", "IT"], ["en", "EN"], ["es", "ES"], ["zh", "中"]].map(([l, label]) => (
              <button
                key={l}
                data-testid={`language-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`px-2 sm:px-3 py-1.5 text-xs font-mono2 uppercase tracking-widest transition-colors duration-300 ${
                  lang === l ? "bg-ember text-black" : "text-mist hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {user ? (
            <div className="relative hidden sm:block" ref={profileRef}>
              <button
                data-testid="nav-profile-button"
                onClick={() => setProfileOpen(!profileOpen)}
                aria-expanded={profileOpen}
                className="flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 pl-1.5 pr-3 py-1.5 hover:bg-gold/20 transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-ember text-black flex items-center justify-center text-xs font-bold">
                  {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                </span>
                <span className="text-xs font-mono2 text-gold max-w-[110px] truncate">{user.name || user.email}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gold transition-transform ${profileOpen ? "rotate-180" : ""}`} />
              </button>
              {profileOpen && (
                <div
                  data-testid="nav-profile-menu"
                  className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-white/10 bg-[#12111a]/95 backdrop-blur-xl shadow-2xl overflow-hidden"
                >
                  <div className="px-5 py-4 border-b border-white/10">
                    <p className="text-sm font-semibold truncate" data-testid="nav-profile-name">{user.name || user.email}</p>
                    <p className="text-[11px] text-mute truncate">{user.email}</p>
                    <div className="mt-2">{roleBadge}</div>
                  </div>
                  <Link to="/dashboard" data-testid="nav-profile-dashboard"
                    className="flex items-center gap-3 px-5 py-3 text-sm text-mist hover:bg-white/5 hover:text-white transition-colors">
                    <BookOpen className="w-4 h-4 text-ember" /> {t("nav_profile")}
                  </Link>
                  {user.role === "admin" && (
                    <div className="border-t border-white/10 mt-1" data-testid="nav-admin-section">
                      <p className="px-5 pt-3 pb-1 text-[10px] font-mono2 uppercase tracking-widest text-mute/70">{t("nav_admin_section")}</p>
                      <Link to="/bacheca" data-testid="nav-profile-bacheca"
                        className="flex items-center gap-3 px-5 py-3 text-sm text-gold hover:bg-gold/10 transition-colors">
                        <ShieldCheck className="w-4 h-4" /> {t("nav_admin_panel")}
                      </Link>
                    </div>
                  )}
                  <button onClick={doLogout} data-testid="nav-profile-logout"
                    className="w-full flex items-center gap-3 px-5 py-3 text-sm text-mute hover:bg-white/5 hover:text-red-400 transition-colors border-t border-white/10">
                    <LogOut className="w-4 h-4" /> {t("nav_logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/accedi"
              data-testid="nav-login-button"
              className="hidden sm:inline-flex items-center rounded-full bg-ember px-4 py-2 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors duration-300"
            >
              {t("nav_login")}
            </Link>
          )}
          <button
            data-testid="nav-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
            aria-expanded={menuOpen}
            className="lg:hidden w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-mist hover:text-ember hover:border-ember transition-colors"
          >
            {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="lg:hidden absolute inset-x-0 top-full h-[calc(100dvh-4rem)] bg-ink/95 backdrop-blur-xl border-t border-white/10 overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-8 flex flex-col gap-1">
            {links.map((l, i) => (
              <Link
                key={l.id}
                to={l.anchor && pathname !== "/" ? "/#shorts" : l.to}
                onClick={(e) => { l.anchor && goAnchor(e); setMenuOpen(false); }}
                data-testid={`mobile-${l.id}`}
                className="font-display font-bold text-2xl py-3.5 border-b border-white/5 text-white/90 hover:text-ember transition-colors"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                {l.label}
              </Link>
            ))}

            {user ? (
              <div className="pt-6 mt-2 space-y-4" data-testid="mobile-profile-block">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-ember text-black flex items-center justify-center text-sm font-bold">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" data-testid="mobile-profile-name">{user.name || user.email}</p>
                    <div className="mt-1">{roleBadge}</div>
                  </div>
                </div>
                <Link to="/dashboard" data-testid="mobile-nav-user-chip" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 text-mist hover:text-ember transition-colors text-sm">
                  <BookOpen className="w-4 h-4 text-ember" /> {t("nav_profile")}
                </Link>
                {user.role === "admin" && (
                  <div className="border-t border-white/10 pt-4" data-testid="mobile-admin-section">
                    <p className="text-[10px] font-mono2 uppercase tracking-widest text-mute/70 mb-2">{t("nav_admin_section")}</p>
                    <Link to="/bacheca" data-testid="mobile-nav-link-bacheca" onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 text-gold hover:text-ember transition-colors text-sm font-semibold">
                      <ShieldCheck className="w-4 h-4" /> {t("nav_admin_panel")}
                    </Link>
                  </div>
                )}
                <button data-testid="mobile-nav-logout-button" onClick={doLogout}
                  className="flex items-center gap-2 text-mute hover:text-red-400 transition-colors text-sm">
                  <LogOut className="w-4 h-4" /> {t("nav_logout")}
                </button>
              </div>
            ) : (
              <Link to="/accedi" data-testid="mobile-nav-login-button" onClick={() => setMenuOpen(false)}
                className="mt-6 inline-flex justify-center rounded-full bg-ember px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
                {t("nav_login")}
              </Link>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Nav;
